import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  g42_companies,
  g42_entities,
  g42_contract_owner,
} from "../../../utils/constants";
import axiosClient from "../../../api/AxiosClient";
import DateRangePicker from "./DateRangePicker";
import MultiSelectAutocomplete from "./MultiSelectAutocomplete";
import styles from "./filterModal.module.scss";

export interface FilterValues {
  group_company?: string[];
  contract_owner?: string[];
  start_date_with_sd?: string;
  end_date_with_sd?: string;
  start_date_with_ed?: string;
  end_date_with_ed?: string;
  start_date_with_cct_created_at?: string;
  end_date_with_cct_created_at?: string;
  start_date_with_cct_updated_at?: string;
  end_date_with_cct_updated_at?: string;
  start_date?: string;
  end_date?: string;
  entity?: string[];
}

type SectionType = "contracts" | "suppliers" | "sourcing";

interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterValues) => void;
  section: SectionType;
  initialFilters?: FilterValues;
}

// ---- old code (before filter options came from the database), kept for reference ----
// // Convert g42_companies object to array format for dropdown
// const GROUP_COMPANY_OPTIONS = Object.entries(g42_companies).map(
//   ([value, label]) => ({ value, label }),
// );
//
// // Convert g42_entities object to array format for dropdown
// const ENTITY_OPTIONS = Object.entries(g42_entities).map(([value, label]) => ({
//   value,
//   label,
// }));
//
// // Convert g42_contract_owner object to array format for dropdown
// const CONTRACT_OWNER_OPTIONS = Object.entries(g42_contract_owner).map(
//   ([value, label]) => ({ value, label }),
// );
// ---- end old code ----

interface FilterOption {
  value: string;
  label: string;
}

interface FilterOptions {
  group_companies: FilterOption[];
  contract_owners: FilterOption[];
  entities: FilterOption[];
}

// Fallback options, used only if the filter options API is unavailable
const FALLBACK_FILTER_OPTIONS: FilterOptions = {
  group_companies: Object.entries(g42_companies).map(([value, label]) => ({
    value,
    label,
  })),
  contract_owners: Object.entries(g42_contract_owner).map(
    ([value, label]) => ({ value, label }),
  ),
  entities: Object.entries(g42_entities).map(([value, label]) => ({
    value,
    label,
  })),
};

// Options come from the data in the database (GET /procurement-filter-options)
let cachedFilterOptions: FilterOptions | null = null;

// Suppliers Filter Component (Date Range only)
interface SuppliersFiltersProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
}

const SuppliersFilters: React.FC<SuppliersFiltersProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => (
  <Box className={styles.filterSection}>
    <DateRangePicker
      label="Created Date"
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={onStartDateChange}
      onEndDateChange={onEndDateChange}
    />
  </Box>
);

// Sourcing Filter Component (Entity multi-select + Date Range)
interface SourcingFiltersProps {
  entityOptions: FilterOption[];
  entity: string[];
  startDate: string;
  endDate: string;
  onEntityChange: (selected: string[]) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
}

const SourcingFilters: React.FC<SourcingFiltersProps> = ({
  entityOptions,
  entity,
  startDate,
  endDate,
  onEntityChange,
  onStartDateChange,
  onEndDateChange,
}) => (
  <>
    <Box className={styles.filterSection}>
      <MultiSelectAutocomplete
        label="Entity"
        placeholder="Select entities..."
        // old: options={ENTITY_OPTIONS}
        options={entityOptions}
        value={entity}
        onChange={onEntityChange}
      />
    </Box>

    <Box className={styles.filterSection}>
      <DateRangePicker
        label="Created Date"
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
      />
    </Box>
  </>
);

// Contracts Filter Component (Group Company + Contract Owner + Date Ranges)
interface ContractsFiltersProps {
  groupCompanyOptions: FilterOption[];
  contractOwnerOptions: FilterOption[];
  groupCompany: string[];
  contractOwner: string[];
  startDateWithSd: string;
  endDateWithSd: string;
  startDateWithEd: string;
  endDateWithEd: string;
  startDateWithCctCreatedAt: string;
  endDateWithCctCreatedAt: string;
  startDateWithCctUpdatedAt: string;
  endDateWithCctUpdatedAt: string;
  onGroupCompanyChange: (selected: string[]) => void;
  onContractOwnerChange: (selected: string[]) => void;
  onStartDateWithSdChange: (date: string) => void;
  onEndDateWithSdChange: (date: string) => void;
  onStartDateWithEdChange: (date: string) => void;
  onEndDateWithEdChange: (date: string) => void;
  onStartDateWithCctCreatedAtChange: (date: string) => void;
  onEndDateWithCctCreatedAtChange: (date: string) => void;
  onStartDateWithCctUpdatedAtChange: (date: string) => void;
  onEndDateWithCctUpdatedAtChange: (date: string) => void;
}

const ContractsFilters: React.FC<ContractsFiltersProps> = ({
  groupCompanyOptions,
  contractOwnerOptions,
  groupCompany,
  contractOwner,
  startDateWithSd,
  endDateWithSd,
  startDateWithEd,
  endDateWithEd,
  startDateWithCctCreatedAt,
  endDateWithCctCreatedAt,
  startDateWithCctUpdatedAt,
  endDateWithCctUpdatedAt,
  onGroupCompanyChange,
  onContractOwnerChange,
  onStartDateWithSdChange,
  onEndDateWithSdChange,
  onStartDateWithEdChange,
  onEndDateWithEdChange,
  onStartDateWithCctCreatedAtChange,
  onEndDateWithCctCreatedAtChange,
  onStartDateWithCctUpdatedAtChange,
  onEndDateWithCctUpdatedAtChange,
}) => (
  <>
    <Box className={styles.filterSection}>
      <MultiSelectAutocomplete
        label="Group Company"
        placeholder="Select companies..."
        // old: options={GROUP_COMPANY_OPTIONS}
        options={groupCompanyOptions}
        value={groupCompany}
        onChange={onGroupCompanyChange}
      />
    </Box>

    <Box className={styles.filterSection}>
      <MultiSelectAutocomplete
        label="Contract Owner"
        placeholder="Select owners..."
        // old: options={CONTRACT_OWNER_OPTIONS}
        options={contractOwnerOptions}
        value={contractOwner}
        onChange={onContractOwnerChange}
      />
    </Box>

    <Box className={styles.filterSection}>
      <DateRangePicker
        label="Contract Created Date"
        startDate={startDateWithCctCreatedAt}
        endDate={endDateWithCctCreatedAt}
        onStartDateChange={onStartDateWithCctCreatedAtChange}
        onEndDateChange={onEndDateWithCctCreatedAtChange}
      />
    </Box>

    <Box className={styles.filterSection}>
      <DateRangePicker
        label="Contract Completed Date"
        startDate={startDateWithCctUpdatedAt}
        endDate={endDateWithCctUpdatedAt}
        onStartDateChange={onStartDateWithCctUpdatedAtChange}
        onEndDateChange={onEndDateWithCctUpdatedAtChange}
      />
    </Box>

    <Box className={styles.filterSection}>
      <DateRangePicker
        label="Contract Start Date"
        startDate={startDateWithSd}
        endDate={endDateWithSd}
        onStartDateChange={onStartDateWithSdChange}
        onEndDateChange={onEndDateWithSdChange}
      />
    </Box>

    <Box className={styles.filterSection}>
      <DateRangePicker
        label="Contract End Date"
        startDate={startDateWithEd}
        endDate={endDateWithEd}
        onStartDateChange={onStartDateWithEdChange}
        onEndDateChange={onEndDateWithEdChange}
        minStartDate={startDateWithSd || undefined}
        minEndDate={endDateWithSd || undefined}
      />
    </Box>
  </>
);

const FilterModal: React.FC<FilterModalProps> = ({
  open,
  onClose,
  onApply,
  section,
  initialFilters = {},
}) => {
  const [groupCompany, setGroupCompany] = useState<string[]>(
    initialFilters.group_company || [],
  );
  const [contractOwner, setContractOwner] = useState<string[]>(
    initialFilters.contract_owner || [],
  );
  const [startDateWithSd, setStartDateWithSd] = useState<string>(
    initialFilters.start_date_with_sd || "",
  );
  const [endDateWithSd, setEndDateWithSd] = useState<string>(
    initialFilters.end_date_with_sd || "",
  );
  const [startDateWithEd, setStartDateWithEd] = useState<string>(
    initialFilters.start_date_with_ed || "",
  );
  const [endDateWithEd, setEndDateWithEd] = useState<string>(
    initialFilters.end_date_with_ed || "",
  );
  const [startDateWithCctCreatedAt, setStartDateWithCctCreatedAt] =
    useState<string>(initialFilters.start_date_with_cct_created_at || "");
  const [endDateWithCctCreatedAt, setEndDateWithCctCreatedAt] =
    useState<string>(initialFilters.end_date_with_cct_created_at || "");
  const [startDateWithCctUpdatedAt, setStartDateWithCctUpdatedAt] =
    useState<string>(initialFilters.start_date_with_cct_updated_at || "");
  const [endDateWithCctUpdatedAt, setEndDateWithCctUpdatedAt] =
    useState<string>(initialFilters.end_date_with_cct_updated_at || "");
  const [startDate, setStartDate] = useState<string>(
    initialFilters.start_date || "",
  );
  const [endDate, setEndDate] = useState<string>(initialFilters.end_date || "");
  const [entity, setEntity] = useState<string[]>(initialFilters.entity || []);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(
    cachedFilterOptions || FALLBACK_FILTER_OPTIONS,
  );

  // Load filter options from the database the first time the modal opens
  useEffect(() => {
    if (!open || cachedFilterOptions) return;
    let cancelled = false;
    axiosClient
      .get<FilterOptions>("/procurement-filter-options")
      .then((response) => {
        cachedFilterOptions = response.data;
        if (!cancelled) setFilterOptions(response.data);
      })
      .catch((error) => {
        console.error("Failed to load procurement filter options", error);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Update local state when modal opens or initialFilters changes
  useEffect(() => {
    if (open) {
      setGroupCompany(initialFilters.group_company || []);
      setContractOwner(initialFilters.contract_owner || []);
      setStartDateWithSd(initialFilters.start_date_with_sd || "");
      setEndDateWithSd(initialFilters.end_date_with_sd || "");
      setStartDateWithEd(initialFilters.start_date_with_ed || "");
      setEndDateWithEd(initialFilters.end_date_with_ed || "");
      setStartDateWithCctCreatedAt(
        initialFilters.start_date_with_cct_created_at || "",
      );
      setEndDateWithCctCreatedAt(
        initialFilters.end_date_with_cct_created_at || "",
      );
      setStartDateWithCctUpdatedAt(
        initialFilters.start_date_with_cct_updated_at || "",
      );
      setEndDateWithCctUpdatedAt(
        initialFilters.end_date_with_cct_updated_at || "",
      );
      setStartDate(initialFilters.start_date || "");
      setEndDate(initialFilters.end_date || "");
      setEntity(initialFilters.entity || []);
    }
  }, [open, initialFilters]);

  const handleGroupCompanyChange = (selected: string[]) => {
    setGroupCompany(selected);
  };

  const handleContractOwnerChange = (selected: string[]) => {
    setContractOwner(selected);
  };

  const handleStartDateWithSdChange = (date: string) => {
    // If new start date of Start Date range is greater than start date of End Date range, clear End Date range
    if (date && startDateWithEd && date > startDateWithEd) {
      setStartDateWithEd("");
      setEndDateWithEd("");
    }
    setStartDateWithSd(date);
  };

  const handleEndDateWithSdChange = (date: string) => {
    // If new end date of Start Date range is greater than end date of End Date range, clear End Date range's end date
    if (date && endDateWithEd && date > endDateWithEd) {
      setEndDateWithEd("");
    }
    setEndDateWithSd(date);
  };

  const handleStartDateWithEdChange = (date: string) => {
    // If new start date of End Date range is less than start date of Start Date range, clear it
    if (date && startDateWithSd && date < startDateWithSd) {
      setStartDateWithEd("");
      return;
    }
    setStartDateWithEd(date);
  };

  const handleEndDateWithEdChange = (date: string) => {
    // If new end date of End Date range is less than end date of Start Date range, clear it
    if (date && endDateWithSd && date < endDateWithSd) {
      setEndDateWithEd("");
      return;
    }
    setEndDateWithEd(date);
  };

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: string) => {
    setEndDate(date);
  };

  const handleEntityChange = (selected: string[]) => {
    setEntity(selected);
  };

  const handleStartDateWithCctCreatedAtChange = (date: string) => {
    setStartDateWithCctCreatedAt(date);
  };

  const handleEndDateWithCctCreatedAtChange = (date: string) => {
    setEndDateWithCctCreatedAt(date);
  };

  const handleStartDateWithCctUpdatedAtChange = (date: string) => {
    setStartDateWithCctUpdatedAt(date);
  };

  const handleEndDateWithCctUpdatedAtChange = (date: string) => {
    setEndDateWithCctUpdatedAt(date);
  };

  const handleClearAll = () => {
    const filters: FilterValues = {};
    onApply(filters);
    onClose();
  };

  const handleApply = () => {
    const filters: FilterValues = {};

    if (section === "contracts") {
      if (groupCompany.length > 0) filters.group_company = groupCompany;
      if (contractOwner.length > 0) filters.contract_owner = contractOwner;
      if (startDateWithSd) filters.start_date_with_sd = startDateWithSd;
      if (endDateWithSd) filters.end_date_with_sd = endDateWithSd;
      if (startDateWithEd) filters.start_date_with_ed = startDateWithEd;
      if (endDateWithEd) filters.end_date_with_ed = endDateWithEd;
      if (startDateWithCctCreatedAt)
        filters.start_date_with_cct_created_at = startDateWithCctCreatedAt;
      if (endDateWithCctCreatedAt)
        filters.end_date_with_cct_created_at = endDateWithCctCreatedAt;
      if (startDateWithCctUpdatedAt)
        filters.start_date_with_cct_updated_at = startDateWithCctUpdatedAt;
      if (endDateWithCctUpdatedAt)
        filters.end_date_with_cct_updated_at = endDateWithCctUpdatedAt;
    } else if (section === "suppliers") {
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
    } else {
      if (entity.length > 0) filters.entity = entity;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
    }

    onApply(filters);
    onClose();
  };

  const hasActiveFilters =
    section === "contracts"
      ? groupCompany.length > 0 ||
        contractOwner.length > 0 ||
        startDateWithSd !== "" ||
        endDateWithSd !== "" ||
        startDateWithEd !== "" ||
        endDateWithEd !== "" ||
        startDateWithCctCreatedAt !== "" ||
        endDateWithCctCreatedAt !== "" ||
        startDateWithCctUpdatedAt !== "" ||
        endDateWithCctUpdatedAt !== ""
      : section === "suppliers"
        ? startDate !== "" || endDate !== ""
        : entity.length > 0 || startDate !== "" || endDate !== "";

  // Validation: Check if date ranges are valid (both dates filled or both empty)
  const isDateRangeValid =
    section === "contracts"
      ? ((startDateWithSd === "" && endDateWithSd === "") ||
          (startDateWithSd !== "" && endDateWithSd !== "")) &&
        ((startDateWithEd === "" && endDateWithEd === "") ||
          (startDateWithEd !== "" && endDateWithEd !== "")) &&
        ((startDateWithCctCreatedAt === "" && endDateWithCctCreatedAt === "") ||
          (startDateWithCctCreatedAt !== "" &&
            endDateWithCctCreatedAt !== "")) &&
        ((startDateWithCctUpdatedAt === "" && endDateWithCctUpdatedAt === "") ||
          (startDateWithCctUpdatedAt !== "" &&
            endDateWithCctUpdatedAt !== "")) &&
        // Cross-range validation: End Date range should be >= Start Date range
        (startDateWithSd === "" ||
          startDateWithEd === "" ||
          startDateWithEd >= startDateWithSd) &&
        (endDateWithSd === "" ||
          endDateWithEd === "" ||
          endDateWithEd >= endDateWithSd)
      : (startDate === "" && endDate === "") ||
        (startDate !== "" && endDate !== "");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      className={styles.dialog}
    >
      <DialogTitle className={styles.dialogTitle}>
        Filters
        <IconButton
          aria-label="close"
          onClick={onClose}
          className={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        {section === "contracts" ? (
          <ContractsFilters
            groupCompanyOptions={filterOptions.group_companies}
            contractOwnerOptions={filterOptions.contract_owners}
            groupCompany={groupCompany}
            contractOwner={contractOwner}
            startDateWithSd={startDateWithSd}
            endDateWithSd={endDateWithSd}
            startDateWithEd={startDateWithEd}
            endDateWithEd={endDateWithEd}
            startDateWithCctCreatedAt={startDateWithCctCreatedAt}
            endDateWithCctCreatedAt={endDateWithCctCreatedAt}
            startDateWithCctUpdatedAt={startDateWithCctUpdatedAt}
            endDateWithCctUpdatedAt={endDateWithCctUpdatedAt}
            onGroupCompanyChange={handleGroupCompanyChange}
            onContractOwnerChange={handleContractOwnerChange}
            onStartDateWithSdChange={handleStartDateWithSdChange}
            onEndDateWithSdChange={handleEndDateWithSdChange}
            onStartDateWithEdChange={handleStartDateWithEdChange}
            onEndDateWithEdChange={handleEndDateWithEdChange}
            onStartDateWithCctCreatedAtChange={
              handleStartDateWithCctCreatedAtChange
            }
            onEndDateWithCctCreatedAtChange={
              handleEndDateWithCctCreatedAtChange
            }
            onStartDateWithCctUpdatedAtChange={
              handleStartDateWithCctUpdatedAtChange
            }
            onEndDateWithCctUpdatedAtChange={
              handleEndDateWithCctUpdatedAtChange
            }
          />
        ) : section === "suppliers" ? (
          <SuppliersFilters
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
        ) : (
          <SourcingFilters
            entityOptions={filterOptions.entities}
            entity={entity}
            startDate={startDate}
            endDate={endDate}
            onEntityChange={handleEntityChange}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
        )}
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button
          onClick={handleClearAll}
          variant="outlined"
          disabled={!hasActiveFilters}
        >
          Clear All
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          disabled={!isDateRangeValid || !hasActiveFilters}
        >
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterModal;
export type { SectionType };
